import { useState } from "react";
import { ShoppingBag, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { buySample } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

export const SampleCard = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptCGV, setAcceptCGV] = useState(false);

  const handleBuy = async () => {
    if (!acceptCGV) {
      toast.error(t("sample.cgvRequired"));
      return;
    }
    setLoading(true);
    try {
      const { url } = await buySample(email);
      if (url) window.location.href = url;
      else toast.error(t("sample.errorPayment"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("sample.errorPayment"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="echantillon"
      data-testid="sample-section"
      className="py-20 sm:py-28 bg-[#0d0c0b] relative overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] hairline" />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Visual block */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-square max-w-[480px] mx-auto lg:mx-0 border border-[#b87333]/30 bg-gradient-to-br from-[#f4ede0] via-[#ebe2cf] to-[#d8cdb6] overflow-hidden">
              <img
                src="/media/sample-product.png"
                alt={t("sample.imageAlt")}
                data-testid="sample-image"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-4 right-4 px-3 py-1 border border-[#b87333]/60 bg-[#0a0a0a]/85 backdrop-blur text-[10px] uppercase tracking-[0.28em] text-[#d89557]">
                {t("sample.included")}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.32em] text-[#d89557]">
              {t("sample.label")}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#f4ede0] mt-4 leading-[1.1]">
              {t("sample.title1")}{" "}
              <span className="copper-text italic">{t("sample.title2")}</span>
            </h2>

            <p className="text-sm sm:text-base text-[#cbc1ad] mt-5 max-w-xl leading-relaxed">
              {t("sample.desc")}
            </p>

            {/* Bullets */}
            <ul className="mt-6 space-y-2 text-sm text-[#cbc1ad]">
              <li className="flex items-center gap-3">
                <span className="h-1 w-3 bg-[#b87333] flex-shrink-0" />
                {t("sample.bullet1")}
              </li>
              <li className="flex items-center gap-3">
                <span className="h-1 w-3 bg-[#b87333] flex-shrink-0" />
                {t("sample.bullet2")}
              </li>
              <li className="flex items-center gap-3">
                <span className="h-1 w-3 bg-[#b87333] flex-shrink-0" />
                {t("sample.bullet3")}
              </li>
            </ul>

            <div className="mt-8 p-6 border border-[#b87333]/30 bg-[#0a0a0a]">
              <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#7a716a]">
                  {t("sample.priceLabel")}
                </span>
                <div>
                  <span data-testid="sample-price" className="font-display text-4xl copper-text">
                    CHF 50.00
                  </span>
                  <span className="text-xs text-[#d89557] ml-2">{t("sample.vatIncl")}</span>
                </div>
              </div>

              <input
                data-testid="sample-email"
                type="email"
                placeholder={t("sample.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0d0c0b] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-sm text-[#f4ede0] placeholder:text-[#7a716a] transition"
              />

              {/* CGV mandatory acceptance */}
              <label className="mt-3 flex items-start gap-3 cursor-pointer group" data-testid="sample-cgv-label">
                <input
                  data-testid="sample-accept-cgv-checkbox"
                  type="checkbox"
                  checked={acceptCGV}
                  onChange={(e) => setAcceptCGV(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#b87333] cursor-pointer"
                />
                <span className="text-xs text-[#cbc1ad] leading-relaxed select-none">
                  {t("config.cgvLabel1")}{" "}
                  <Link
                    to="/cgv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#d89557] underline underline-offset-2 hover:text-[#f4ede0] transition"
                  >
                    {t("config.cgvLabel2")}
                  </Link>{" "}
                  {t("config.cgvLabel3")}
                </span>
              </label>

              <button
                data-testid="sample-buy-button"
                disabled={loading || !acceptCGV}
                onClick={handleBuy}
                className="btn-copper w-full mt-3 py-4 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShoppingBag className="w-4 h-4" />
                )}
                {t("sample.cta")}
              </button>

              <p className="text-[10px] text-center text-[#7a716a] mt-3 uppercase tracking-[0.25em]">
                {t("sample.footer")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
